import React, { useState } from "react";
import { Add, ImportExport } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { Layout } from "../Layouts/Layout";
import { TeamList } from "../Components/Team/List";
import { Team as TeamType } from "../types";
import { SectionSelector } from "../Components/Section/Selector";
import {
  GET_SECTIONS,
  SectionsResult,
  GET_GROUPS,
  GroupsResult,
  UPDATE_GROUP,
  SYNC_COURSE,
  CourseResult,
  GET_COURSE,
  SET_COURSE_MODE,
  GET_ALL_AND_SECTION_USERS,
  TeamsUsersResult,
} from "../Queries";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { ImportModal } from "../Components/Team/ImportModal";
import { LoadingButton } from "@mui/lab";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { TeamRoster } from "../Components/Team/Roster";
import { TeamNew } from "../Components/Team/New";

export const RosterEmpty = () => {
  return (
    <Card>
      <CardHeader title="Great job!" />
      <CardContent>
        <Typography>
          You have successfully assigned your entire roster.
        </Typography>
      </CardContent>
    </Card>
  );
};

export const Teams = () => {
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  //
  // State
  //

  const [importOpen, setImportOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [sectionId, setSectionId] = useState(undefined as string | undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [showForm, setShowForm] = useState<boolean>(false);

  //
  // Queries
  //

  const { loading: sectionsLoading, data: sectionsData } =
    useQuery<SectionsResult>(GET_SECTIONS, {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.sections.list"), error),
    });

  const {
    loading: courseLoading,
    data: courseData,
    refetch: refetchCourse,
  } = useQuery<CourseResult>(GET_COURSE, {
    onError: (error) => setError(t("errors.courses.get"), error),
  });

  const { loading: groupsLoading, data: groupsData } = useQuery<GroupsResult>(
    GET_GROUPS,
    {
      variables: {
        filters: {
          // section: sectionId ? sectionId : undefined,
        },
      },
      onError: (error) => setError(t("errors.groups.list"), error),
    },
  );

  const teams = groupsData ? groupsData.groups : undefined;

  const { loading: usersLoading, data: usersData } = useQuery<TeamsUsersResult>(
    GET_ALL_AND_SECTION_USERS,
    {
      variables: {
        filters: {
          sections: sectionId ? sectionId : undefined,
          // unassigned: true
        },
      },
      onError: (error) => setError(t("errors.users.list"), error),
    },
  );

  //
  // Mutations
  //

  const [updateGroup] = useMutation(UPDATE_GROUP, {
    //refetchQueries: ["GetGroups"],
    onError: (error) => setError(t("errors.groups.update"), error),
  });

  const [syncCourse] = useMutation(SYNC_COURSE, {
    refetchQueries: ["GetGroups", "GetSections"],
    onError: (error) => setError(t("errors.courses.sync"), error),
  });
  const [setCourseMode] = useMutation(SET_COURSE_MODE, {
    // refetchQueries: [""]
    onError: (error) => setError(t("errors.courses.setMode"), error),
  });

  const course = courseData ? courseData.course : undefined;
  const sections = sectionsData ? sectionsData.sections : undefined;
  const section = sections
    ? sections.find((target) => target.id === sectionId)
    : undefined;

  if (!sectionsLoading && !sections) {
    throw new Error("No sections found");
  }

  const sectionStudents = usersData?.sectionUsers.filter(
    (user) => user.isLearner,
  );
  const allStudents = usersData?.allUsers.filter((user) => user.isLearner);

  //
  // Event Handlers
  //

  function handleTeamChange(team: TeamType) {
    updateGroup({
      variables: team,
      context: {
        debounceKey: `team.${team.id}`,
      },
      optimisticResponse: {
        updateGroup: {
          id: team.id,
          __typename: "Group",
          name: team.name,
          userIds: team.userIds,
        },
      },
    });
  }

  function handleImportTeams() {
    if (course?.fullSyncEnabled && course?.fullSyncReady) {
      importTeams();
    } else {
      setImportOpen(true);
    }
  }

  function handleImportClose() {
    setImportOpen(false);
  }

  function handleImportConnected() {
    refetchCourse();
  }

  async function handleImportEnable(enabled: boolean) {
    await setCourseMode({
      variables: {
        fullSync: enabled,
      },
    });

    setImportOpen(false);
    importTeams();
  }

  function handleAssignment(userIds: string[], team: TeamType) {
    handleTeamChange({ ...team, userIds: [...team.userIds, ...userIds] });
    setSelected([]);
  }

  //
  // Service
  //

  async function importTeams() {
    setIsImporting(true);
    await syncCourse();
    setIsImporting(false);
  }

  const showLoading = sectionsLoading || groupsLoading || usersLoading;

  return (
    <Layout
      breadcrumbs={[
        { title: t("breadcrumbs.teams") },
        { title: t("breadcrumbs.teamList") },
      ]}
      loading={showLoading}
    >
      {importOpen && course && (
        <ImportModal
          course={course}
          onClose={handleImportClose}
          onConnect={handleImportConnected}
          onEnable={handleImportEnable}
        />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        {!sectionsLoading && sections ? (
          <SectionSelector
            sections={sections}
            selected={section}
            onChange={(section) => setSectionId(section ? section.id : "")}
          />
        ) : (
          <>Sections loading...</>
        )}
        <Typography sx={{ ml: 2 }}>
          {(teams ?? []).length} Team{teams?.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip
            title={
              courseLoading || course === undefined
                ? ""
                : !course.fullSyncCapable
                  ? "Your LMS isn't configured for full API access; please contact an administrator to enable full sync"
                  : "Import teams and sections from your LMS"
            }
          >
            <span>
              <LoadingButton
                disabled={
                  courseLoading ||
                  course === undefined ||
                  !course.fullSyncCapable
                }
                variant="contained"
                aria-label="import teams"
                sx={{ mr: 1 }}
                endIcon={<ImportExport />}
                onClick={handleImportTeams}
                loading={isImporting}
              >
                {isImporting ? "Importing..." : "Import Teams"}
              </LoadingButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            aria-label="start new team"
            endIcon={<Add />}
            onClick={() => setShowForm(true)}
          >
            New Team
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <TeamRoster
          teams={teams}
          users={sectionStudents}
          selectedUsers={selected}
          isLoading={usersLoading}
          onAssign={handleAssignment}
          onSelect={setSelected}
        />
        <Grid item xs={12} md={6} lg={6}>
          {showForm && (
            <TeamNew
              onClose={() => setShowForm(false)}
              users={sectionStudents || []}
            />
          )}
          <TeamList
            teams={teams}
            sectionStudents={sectionStudents}
            allStudents={allStudents}
            selectedSection={section}
            isCreating={showForm}
            isLoading={groupsLoading}
            onNew={() => setShowForm(true)}
            onAssign={handleAssignment}
            onChange={handleTeamChange}
          />
        </Grid>
      </Grid>
    </Layout>
  );
};
